<template>
	<v-expansion-panels v-model="panel" flat accordion>
		<ExpansionPanel :title="'Basics'">
			<RadioMenu class="mb-4" :options="recipientTypeOptions" :value="paramsMap?.is_inbox_mode" @selected="setIsInboxMode" />
			<div v-if="paramsMap !== null">
				<div v-for="param in params" :key="`${editNode.id}_${param.key}`">
					<ParamDispatcherDataProvider
						:param="param"
						:node="{ ...editNode, params_map: paramsMap }"
						:workflowVersion="editedWorkflowVersion"
						@update-input-field="updateInputField"
						@update-input-field-label="updateInputFieldLabel"
						v-if="shouldShowParam(param)"
					/>
				</div>
			</div>
		</ExpansionPanel>
		<ExpansionPanel title="Customize">
			<CustomizeNotificationSettings :iconMetadata="notification.icon_metadata" @change-icon="onChangeIcon" />
		</ExpansionPanel>
		<ExpansionPanel :title="'Channels options'" v-if="!isInboxMode">
			<NotificationChannelConfig
				v-for="channel in visibleChannels"
				:channel="channel"
				:channelConfig="getChannelConfig(channel.id)"
				:key="channel.id"
				@update:channelIsOn="(val) => setChannelIsOn(channel.id, val)"
			/>
		</ExpansionPanel>
	</v-expansion-panels>
</template>

<script>
import debounce from 'lodash/debounce';

import { FeatureTypes } from '@/components/Billing/billing-types';
import ParamDispatcherDataProvider from '@/components/BlockEditor/BlockEditorParams/ParamDispatcherDataProvider';
import CustomizeNotificationSettings from '@/components/BlockEditorNotification/CustomizeNotificationSettings.vue';
import NotificationChannelConfig from '@/components/BlockEditorNotification/NotificationChannelConfig.vue';
import RadioMenu from '@/components/BlockEditorNotification/RadioMenu.vue';
import ExpansionPanel from '@/components/BlockEditorWidget/FieldSettings/WidgetSettings/ExpansionPanel';
import { getActionParams } from '@/store/modules/actions/getters';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { getNode } from '@/store/modules/nodes/getters';
import { fetchChannels } from '@/store/modules/notifications/actions';
import { channels } from '@/store/modules/notifications/getters';
import { setParamInEditNode, updateNotification } from '@/store/modules/selectedNode/actions';
import { editNode, notification } from '@/store/modules/selectedNode/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';

const DEBOUNCE_TIME = 600;

export const PARAM_RECIPIENT = 'recipient';
export const PARAM_INBOX = 'inbox';

export default {
	name: 'NotificationSettings',
	components: {
		ExpansionPanel,
		ParamDispatcherDataProvider,
		NotificationChannelConfig,
		RadioMenu,
		CustomizeNotificationSettings,
	},
	computed: {
		...mapGettersShorthand({
			editedWorkflowVersion,
			editNode,
			notification,
			getServiceBySlug,
			getNode,
			getActionParams,
			channels,
		}),
		params() {
			return this.getActionParams(this.editNode.action);
		},
		isInboxMode() {
			return this.paramsMap?.is_inbox_mode;
		},
		visibleChannels() {
			return this.channels.filter(({ is_inbox }) => !is_inbox);
		},
	},
	methods: {
		...mapActionsShorthand({ fetchChannels, setParamInEditNode, updateNotification }),
		onChangeIcon(payload) {
			const { icon, entity_id, source } = payload;
			this.updateInputField({ map: { key: 'icon', value: icon } });
			this.updateInputField({ map: { key: 'icon_metadata', value: { entity_id, source } } });
		},
		async updateInputField({ map: { key, value } }) {
			this.$set(this.paramsMap, key, value);
			this.debouncedUpdateNotification();
		},
		updateInputFieldLabel({ label: { key, value } }) {
			const newParamsMapLabels = { ...this.editNode.params_map_labels, [key]: value };
			this.setParamInEditNode({ key: 'params_map_labels', value: newParamsMapLabels });
		},
		getLocalChannelConfig(channelId) {
			return this.paramsMap.channel_configs.find(({ channel }) => channel === channelId);
		},
		getChannelConfig(channelId) {
			return this.notification?.channel_configs?.find(({ channel }) => channel === channelId);
		},
		setChannelIsOn(channelId, isOn) {
			this.getLocalChannelConfig(channelId).is_on = isOn;
			this.debouncedUpdateNotification();
		},
		buildDefaultChannelConfig(channelId) {
			return { channel: channelId, is_on: false, cred: null };
		},
		setIsInboxMode(value) {
			this.paramsMap.is_inbox_mode = value;
			this.debouncedUpdateNotification();
		},
		shouldShowParam({ key }) {
			if (key === PARAM_INBOX) {
				return this.isInboxMode;
			} else if (key === PARAM_RECIPIENT) {
				return !this.isInboxMode;
			}
			return true;
		},
	},
	async created() {
		await this.fetchChannels();
		this.debouncedUpdateNotification = debounce(() => this.updateNotification({ fields: this.paramsMap }), DEBOUNCE_TIME);
		const paramsMap = JSON.parse(JSON.stringify({ ...this.notification }));
		paramsMap.channel_configs = Object.values(this.channels || []).map(
			({ id }) => this.getChannelConfig(id) || this.buildDefaultChannelConfig(id)
		);
		this.paramsMap = paramsMap;
	},
	data() {
		return {
			panel: 0,
			paramsMap: null,
			recipientTypeOptions: [
				{ text: 'Individual', value: false },
				{ text: 'Shared inbox', value: true, billingFeatureId: FeatureTypes.SharedNotificationInboxes },
			],
		};
	},
};
</script>
<style scoped>
.expansion-container .panel-title {
	font-size: 16px;
}
</style>
